import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Package, Users, Settings as SettingsIcon, Store, Key, Link2, UserCheck, Radio, GitCompare, Plug, Wrench } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import ShopManagement from './OCIShopManagement/ShopManagement';
import UserManagement from './UserManagement/UserManagement';
import GeneralSettings from './GeneralSettings';
import InternalShopManagement from './InternalShopManagement';
import InternalShopProductManagement from './InternalShopManagement/ProductManagement/InternalShopProductManagement';
import LicenseSettings from './LicenseSettings';
import MappingSettings from './MappingSettings';
import PermissionProfilesSettings from './PermissionProfilesSettings';
import PmSettingsPanel from './PmSettingsPanel';
import AdapterTestPanel from './AdapterTestPanel';
import AbAdapterComparePanel from './AbAdapterComparePanel';
import SapConnectivityPanel from './SapConnectivityPanel';


const AdminView = ({ user, licenseState, ociShops, adminUsers, onBack, onEditShop, onDeleteShop, onReloadUsers, onUpdateUser, activeTab = 'general', onTabChange, showSuccess, showError, showWarning, showInfo }) => {
  const [managingProductsShop, setManagingProductsShop] = useState(null);
  const [internalShopsCount, setInternalShopsCount] = useState(0);
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  
  // Interne Shops beim ersten Laden zählen
  useEffect(() => {
    const loadInternalShopsCount = async () => {
      try {
        const response = await fetch('/api/internal-shops', {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        if (response.ok) {
          const shops = await response.json();
          setInternalShopsCount(shops?.length || 0);
          console.log('✅ Internal shops count loaded:', shops?.length || 0);
        }
      } catch (error) {
        console.error('Error loading internal shops count:', error);
        setInternalShopsCount(0);
      }
    };

    if (user?.token) {
      loadInternalShopsCount();
    }
  }, [user?.token]);
  
  // Debug-Logging für Props
  useEffect(() => {
    console.log('AdminView Props:', {
      userRole: user?.role,
      ociShopsCount: ociShops?.length,
      adminUsersCount: adminUsers?.length,
      adminUsers: adminUsers,
      activeTab: activeTab,
      internalShopsCount: internalShopsCount
    });
  }, [user, ociShops, adminUsers, activeTab, internalShopsCount]);
  
  const handleSettingsSaved = (logo, siteTitle) => {
    // Logo wird jetzt über globalLogo in App.js verwaltet, nicht über user
    // Keine Notwendigkeit, das User-Objekt zu aktualisieren
    console.log('✅ Settings saved in AdminView - logo will be updated globally');
  };

  const handleBackFromProducts = () => {
    setManagingProductsShop(null);
  };

  const handleManageInternalProducts = (shop) => {
    setManagingProductsShop({ ...shop, type: 'internal' });
  };

  const handleInternalShopsCountUpdate = (count) => {
    console.log('🔄 Updating internal shops count to:', count);
    setInternalShopsCount(count);
  };

  // Sicherstellen, dass adminUsers ein Array ist
  const safeAdminUsers = Array.isArray(adminUsers) ? adminUsers : [];

  // Produktverwaltung anzeigen
  if (managingProductsShop) {
    if (managingProductsShop.type === 'internal') {
      return (
        <InternalShopProductManagement
          shop={managingProductsShop}
          user={user}
          onBack={handleBackFromProducts}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
        />
      );
    } else {
      // Für OCI-Shops gibt es keine separate Produktverwaltung mehr
      console.log('OCI-Shop product management not available');
      setManagingProductsShop(null);
      return null;
    }
  }
  
  return (
    <div className={`${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black'
      : 'bg-white'
    }`}>
      {/* Liquid Glass Header Section */}
      <div className="relative">
        {/* Liquid Glass Header Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl'
          : 'bg-white border-b border-slate-200'
        } shadow-sm`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-r from-blue-900/5 via-transparent to-purple-900/5'
          : ''
        }`}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Liquid Glass Admin Icon */}
              <div className="relative group/icon">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm"></div>
                <div className="relative bg-gradient-to-br from-red-600/60 via-red-700/50 to-red-800/60 backdrop-blur-xl w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_6px_24px_rgba(239,68,68,0.25),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20">
                  <Shield className="w-5 h-5 text-white/90 drop-shadow-lg" />
                  <div className="absolute top-0.5 left-0.5 w-2.5 h-2.5 bg-white/30 rounded-full blur-sm"></div>
                </div>
              </div>
              <div>
                <h1 className={`text-2xl font-semibold ${isDarkMode
                  ? 'bg-gradient-to-r from-white via-red-200 to-white bg-clip-text text-transparent drop-shadow-lg'
                  : 'text-slate-900'
                }`}>{t('admin.title')}</h1>
              </div>
            </div>
            
            {/* Liquid Glass Back Button */}
            <button
              onClick={onBack}
              className={`relative group/back overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'}`}
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)'
                  : undefined,
                backdropFilter: isDarkMode ? 'blur(20px)' : undefined,
                border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : undefined
              }}
            >
              {/* Smooth Hover Overlay */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/back:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                    : undefined
                }}
              ></div>
                            
              <div className={`relative z-10 px-4 py-2 flex items-center font-medium transition-all duration-300 ${
                isDarkMode ? 'text-white drop-shadow-lg group-hover/back:drop-shadow-xl' : 'text-slate-700'
              }`}>
                <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/back:scale-110" />
                {t('admin.back')}
              </div>
              
              {/* Smooth Wave Effect */}
              <div className={`absolute inset-0 opacity-0 group-hover/back:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/back:translate-x-full transition-transform duration-800"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                      : undefined
                  }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Liquid Glass Filter Tabs */}
        <div className="relative group/tabs mb-6">
          {/* Glass Background */}
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
            : 'glass-light-card'
          } rounded-3xl`}></div>
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
            : ''
          } rounded-3xl`}></div>
          
          <div className="relative">
            <div className={`border-b ${isDarkMode ? 'border-white/20' : 'border-gray-300/30'}`}>
              <nav className="flex space-x-1 px-4 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => onTabChange('general')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'general'
                      ? `border-red-400 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <SettingsIcon className="w-4 h-4 mr-1.5" />
                  {t('admin.tabSettings')}
                </button>
                <button
                  onClick={() => onTabChange('users')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'users'
                      ? `border-blue-400 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Users className="w-4 h-4 mr-1.5" />
                  {t('admin.tabUsers')}
                </button>
                <button
                  onClick={() => onTabChange('permissions')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'permissions'
                      ? `border-brand ${isDarkMode ? 'text-indigo-300' : 'text-brand'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <UserCheck className="w-4 h-4 mr-1.5" />
                  {t('admin.tabRoles')}
                </button>
                <button
                  onClick={() => onTabChange('shops')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'shops'
                      ? `border-brand ${isDarkMode ? 'text-emerald-300' : 'text-brand'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Package className="w-4 h-4 mr-1.5" />
                  {t('admin.tabOciShops')}
                </button>
                <button
                  onClick={() => onTabChange('internal-shops')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'internal-shops'
                      ? `border-brand ${isDarkMode ? 'text-purple-300' : 'text-brand'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Store className="w-4 h-4 mr-1.5" />
                  {t('admin.tabInternalShops')}
                </button>
                <button
                  onClick={() => onTabChange('mapping')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'mapping'
                      ? `border-brand ${isDarkMode ? 'text-cyan-300' : 'text-brand'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Link2 className="w-4 h-4 mr-1.5" />
                  {t('admin.tabMapping')}
                </button>
                <button
                  onClick={() => onTabChange('license')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'license'
                      ? `border-orange-400 ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Key className="w-4 h-4 mr-1.5" />
                  {t('admin.tabApplication')}
                </button>
                <button
                  onClick={() => onTabChange('adapter')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'adapter'
                      ? `border-teal-400 ${isDarkMode ? 'text-teal-300' : 'text-teal-600'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Radio className="w-4 h-4 mr-1.5" />
                  {t('admin.tabAdapter')}
                </button>
                <button
                  onClick={() => onTabChange('ab-adapter')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'ab-adapter'
                      ? `border-teal-400 ${isDarkMode ? 'text-teal-300' : 'text-teal-600'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <GitCompare className="w-4 h-4 mr-1.5" />
                  {t('admin.tabAbAdapter')}
                </button>
                <button
                  onClick={() => onTabChange('sap-connectivity')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'sap-connectivity'
                      ? `border-teal-400 ${isDarkMode ? 'text-teal-300' : 'text-teal-600'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Plug className="w-4 h-4 mr-1.5" />
                  {t('admin.tabSapConnectivity')}
                </button>
                <button
                  onClick={() => onTabChange('pm')}
                  className={`py-3 px-3 border-b-2 font-medium text-sm transition-all duration-200 drop-shadow-sm flex items-center whitespace-nowrap ${
                    activeTab === 'pm'
                      ? `border-amber-400 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`
                      : `border-transparent ${isDarkMode ? 'text-white/70 hover:text-white hover:border-white/30' : 'text-gray-600 hover:text-gray-800 hover:border-gray-400/50'}`
                  }`}
                >
                  <Wrench className="w-4 h-4 mr-1.5" />
                  {t('admin.tabPm')}
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="transition-all duration-300 ease-in-out">
          {activeTab === 'general' && (
            <GeneralSettings 
              user={user}
              saasMode={['central', 'unknown'].includes(licenseState?.mode)}
              onSettingsSaved={handleSettingsSaved}
            />
          )}

          {activeTab === 'users' && (
            <UserManagement
              user={user}
              adminUsers={safeAdminUsers}
              onReloadUsers={onReloadUsers}
              onUpdateUser={onUpdateUser}
              showSuccess={showSuccess}
              showError={showError}
              showWarning={showWarning}
            />
          )}

          {activeTab === 'shops' && (
            <ShopManagement 
              ociShops={ociShops}
              onEditShop={onEditShop}
              onDeleteShop={onDeleteShop}
            />
          )}

          {activeTab === 'internal-shops' && (
            <InternalShopManagement 
              user={user}
              onManageProducts={handleManageInternalProducts}
              onShopsCountUpdate={handleInternalShopsCountUpdate}
              showSuccess={showSuccess}
              showError={showError}
              showWarning={showWarning}
            />
          )}

          {activeTab === 'license' && (
            <LicenseSettings
              user={user}
              centralManaged={['central', 'unknown'].includes(licenseState?.mode)}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}

          {activeTab === 'mapping' && (
            <MappingSettings
              user={user}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}

          {activeTab === 'permissions' && (
            <PermissionProfilesSettings
              user={user}
              showSuccess={showSuccess}
              showError={showError}
              showWarning={showWarning}
            />
          )}

          {activeTab === 'adapter' && (
            <AdapterTestPanel
              user={user}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}

          {activeTab === 'ab-adapter' && (
            <AbAdapterComparePanel
              user={user}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}

          {activeTab === 'sap-connectivity' && (
            <SapConnectivityPanel
              user={user}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}

          {activeTab === 'pm' && (
            <PmSettingsPanel
              user={user}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminView;